import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import axios from 'axios'
import { apiClient } from '@/services/api'
import { useOffensesBySearchId } from '@/hooks/useAnalytics'
import { Icon } from './Icon'
import { JsonView } from './JsonView'
import { EMPTY_FORM, parseOffenseIds, SAMPLE_FORM } from './sample'
import type { EvaluateResponse, OffenseDecision, PublicRecordsFormInput } from './types'

type FieldPath = 'searchId' | 'searchDate' | 'applicantState' | 'customerState' | 'searchType' | 'offenseIds' | `candidate.${keyof PublicRecordsFormInput['candidate']}`

interface EvaluateResult {
  ok: boolean
  status: number
  data?: EvaluateResponse
  error?: string
  sentBody?: unknown
  complianceEngineInput?: unknown
}

interface LookupResponse {
  ok: boolean
  form: PublicRecordsFormInput
}

function isFormPopulated(form: PublicRecordsFormInput): boolean {
  return Boolean(
    form.searchDate &&
    form.applicantState &&
    parseOffenseIds(form.offenseIds).length > 0 &&
    form.candidate.first_name &&
    form.candidate.last_name &&
    form.xml.trim(),
  )
}
interface ConfigResponse {
  config: {
    apiBaseUrl: string
    environment: 'dev' | 'staging' | 'prod' | null
    gcpProjectId: string
    authMethod: 'env-token' | 'api-key' | null
    hasCredentials: boolean
    tokenSubject: string | null
    tokenTier: string | null
    tokenAlg: string | null
    tokenWarning: string | null
  }
  health: { ok: boolean; status: number; body?: unknown; error?: string }
}

const AUTH_METHOD_LABEL: Record<'env-token' | 'api-key', string> = {
  'env-token': 'PUBLIC_RECORDS_TOKEN',
  'api-key': 'API key exchange',
}

const ENV_BADGE: Record<'dev' | 'staging' | 'prod', { label: string; className: string }> = {
  dev: { label: 'Dev', className: 'bg-sky-50 text-sky-700 ring-sky-200' },
  staging: { label: 'Staging', className: 'bg-amber-50 text-amber-800 ring-amber-200' },
  prod: { label: 'Prod', className: 'bg-rose-50 text-rose-800 ring-rose-200' },
}

function EnvironmentBadge({ environment }: { environment: 'dev' | 'staging' | 'prod' | null | undefined }) {
  if (!environment) return null
  const badge = ENV_BADGE[environment]
  return (
    <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${badge.className}`}>
      {badge.label}
    </span>
  )
}

const US_STATES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA',
  'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
  'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC', 'PR', 'GU', 'VI',
  'AS', 'MP',
])

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

interface XmlInfo {
  ok: boolean
  caseCount: number
  error?: string
}

const RECOGNIZED_XML_ROOTS = new Set(['ScreeningResults', 'xml', 'OrderXML', 'BackgroundReports'])

function xmlRootTag(doc: Document): string {
  const root = doc.documentElement?.tagName ?? ''
  return root.includes(':') ? root.split(':').pop() ?? root : root
}

/** Inspect screening XML for well-formedness and a supported root element. */
function inspectXml(xml: string): XmlInfo {
  if (!xml.trim()) return { ok: false, caseCount: 0, error: 'Required' }
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return { ok: true, caseCount: 0 }
  }
  try {
    const doc = new DOMParser().parseFromString(xml, 'application/xml')
    if (doc.getElementsByTagName('parsererror').length > 0) {
      return { ok: false, caseCount: 0, error: 'XML is not well-formed' }
    }
    const root = xmlRootTag(doc)
    if (!RECOGNIZED_XML_ROOTS.has(root)) {
      return {
        ok: false,
        caseCount: 0,
        error: `Unsupported root <${root || 'unknown'}> — expected ScreeningResults, xml, OrderXML, or BackgroundReports`,
      }
    }
    return { ok: true, caseCount: doc.getElementsByTagName('case').length }
  } catch {
    return { ok: false, caseCount: 0, error: 'Could not parse XML' }
  }
}

type FieldErrors = Partial<Record<FieldPath | 'xml', string>>

function validate(form: PublicRecordsFormInput, xmlInfo: XmlInfo): FieldErrors {
  const e: FieldErrors = {}

  if (!form.searchId.trim()) e.searchId = 'Required'
  if (!form.searchDate) e.searchDate = 'Required'

  const state = form.applicantState.trim().toUpperCase()
  if (!state) e.applicantState = 'Required'
  else if (!US_STATES.has(state)) e.applicantState = 'Use a 2-letter state code'

  if (parseOffenseIds(form.offenseIds).length === 0) e.offenseIds = 'Add at least one offense ID'

  if (!form.candidate.first_name.trim()) e['candidate.first_name'] = 'Required'
  if (!form.candidate.last_name.trim()) e['candidate.last_name'] = 'Required'

  if (!form.candidate.date_of_birth) {
    e['candidate.date_of_birth'] = 'Required'
  } else {
    const dob = new Date(form.candidate.date_of_birth)
    if (Number.isNaN(dob.getTime())) e['candidate.date_of_birth'] = 'Invalid date'
    else if (dob > new Date()) e['candidate.date_of_birth'] = 'Must be in the past'
  }

  const ssnDigits = form.candidate.ssn.replace(/\D/g, '')
  if (!form.candidate.ssn.trim()) e['candidate.ssn'] = 'Required'
  else if (ssnDigits.length !== 9) e['candidate.ssn'] = 'Must be 9 digits'

  if (!xmlInfo.ok) e.xml = xmlInfo.error ?? 'Invalid XML'

  return e
}

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

/** Color treatment for the various queues / decisions in the response. */
function badgeClass(value: string): string {
  const v = value.toLowerCase()
  if (/(^|\b)(automation|not[_ ]reportable|clear|pass)/.test(v)) return 'bg-emerald-50 text-emerald-700 ring-emerald-200'
  if (/(reportable|fail)/.test(v)) return 'bg-red-50 text-red-700 ring-red-200'
  if (/(manual[_ ]review|insufficient|auditor|review|pending)/.test(v)) return 'bg-amber-50 text-amber-700 ring-amber-200'
  return 'bg-[var(--color-surface-2)] text-[var(--color-body)] ring-[var(--color-border)]'
}

function Badge({ value }: { value: string }) {
  return <span className={`rounded-md px-2 py-0.5 font-mono text-xs ring-1 ${badgeClass(value)}`}>{value}</span>
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required,
  error,
  inputMode,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  required?: boolean
  error?: string
  inputMode?: 'text' | 'numeric'
}) {
  const borderClass = error
    ? 'border-red-400 focus:border-red-400 focus:ring-red-500/20'
    : 'border-[var(--color-border)] focus:border-emerald-500 focus:ring-emerald-500/20'
  return (
    <label className="block">
      <span className="text-xs font-medium text-[var(--color-muted)]">
        {label}
        {required ? <span className="text-emerald-600"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full rounded-lg border bg-[var(--color-surface)] px-3 py-2 text-sm font-normal text-[var(--color-ink)] outline-none transition placeholder:text-[var(--color-subtle)] focus:ring-2 ${borderClass}`}
      />
      {error ? <span className="mt-1 block text-xs text-red-600">{error}</span> : null}
    </label>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-xs font-semibold uppercase tracking-wide text-[var(--color-subtle)]">{title}</legend>
      {children}
    </fieldset>
  )
}

function SetupStatus({ data, loading, onRefresh }: { data: ConfigResponse | null; loading: boolean; onRefresh: () => void }) {
  const c = data?.config
  const envBadge = c?.environment ? ENV_BADGE[c.environment] : null
  return (
    <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-elevate-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
            <Icon name="bolt" className="h-3.5 w-3.5" />
          </span>
          <h2 className="text-sm font-semibold text-[var(--color-ink)]">Setup status</h2>
          <EnvironmentBadge environment={c?.environment} />
        </div>
        <button
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs text-[var(--color-muted)] ring-1 ring-[var(--color-border)] transition hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
        >
          {loading ? <span className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-[var(--color-subtle)] border-t-transparent" /> : null}
          {loading ? 'Checking…' : 'Re-check'}
        </button>
      </div>

      <div className="mt-2 divide-y divide-[var(--color-border)]">
        <div className="flex items-center justify-between gap-4 py-2.5">
          <div className="flex items-center gap-2.5">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${
                data?.health.ok ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.18)]' : 'bg-amber-500 shadow-[0_0_0_3px_rgba(245,158,11,0.18)]'
              }`}
            />
            <span className="text-sm text-[var(--color-body)]">
              CRA API{envBadge ? ` · ${envBadge.label}` : ''}
            </span>
          </div>
          <div className="text-right">
            <span className="font-mono text-xs text-[var(--color-muted)]">{c?.apiBaseUrl}</span>
            {c?.gcpProjectId ? (
              <span className="mt-0.5 block font-mono text-[10px] text-[var(--color-subtle)]">{c.gcpProjectId}</span>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 py-2.5">
          <div className="flex items-center gap-2.5">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${
                c?.hasCredentials && c?.authMethod ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.18)]' : 'bg-amber-500 shadow-[0_0_0_3px_rgba(245,158,11,0.18)]'
              }`}
            />
            <span className="text-sm text-[var(--color-body)]">Authentication</span>
          </div>
          <div className="text-right text-sm text-[var(--color-muted)]">
            {c?.hasCredentials ? (
              c.authMethod ? (
                <span>
                  <span className="text-[var(--color-subtle)]">{AUTH_METHOD_LABEL[c.authMethod]}</span>
                  {c.tokenSubject ? (
                    <>
                      <span className="text-[var(--color-subtle)]"> · </span>
                      <span className="font-mono text-xs text-emerald-600">{c.tokenSubject}</span>
                    </>
                  ) : null}
                  {c.tokenTier ? <span className="text-[var(--color-subtle)]"> · {c.tokenTier}</span> : null}
                </span>
              ) : (
                <span className="text-amber-600">credentials set, token not obtained</span>
              )
            ) : (
              <span className="text-amber-600">missing — set PUBLIC_RECORDS_API_KEY or PUBLIC_RECORDS_TOKEN</span>
            )}
          </div>
        </div>
      </div>

      {c?.tokenWarning ? (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 ring-1 ring-amber-200">{c.tokenWarning}</p>
      ) : null}
    </section>
  )
}

export function EvaluateConsole() {
  const [form, setForm] = useState<PublicRecordsFormInput>(EMPTY_FORM)
  const [loadedSearchId, setLoadedSearchId] = useState<string | null>(null)
  const [loadingRecord, setLoadingRecord] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<EvaluateResult | null>(null)
  const [attempted, setAttempted] = useState(false)

  const [config, setConfig] = useState<ConfigResponse | null>(null)
  const [configLoading, setConfigLoading] = useState(true)

  const refreshConfig = useCallback(async () => {
    setConfigLoading(true)
    try {
      const res = await apiClient.get<ConfigResponse>('/public-records/config')
      setConfig(res)
    } catch {
      setConfig(null)
    } finally {
      setConfigLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshConfig()
  }, [refreshConfig])

  const set = (path: FieldPath, value: string) => {
    setForm((prev) => {
      const next = structuredClone(prev) as unknown as Record<string, unknown>
      const [group, key] = path.split('.') as [string, string]
      if (key === undefined) {
        next[group] = value
      } else {
        ;(next[group] as Record<string, unknown>)[key] = value
      }
      return next as unknown as PublicRecordsFormInput
    })
  }

  const xmlInfo = useMemo(() => inspectXml(form.xml), [form.xml])
  const errors = useMemo(() => validate(form, xmlInfo), [form, xmlInfo])
  const errorCount = Object.keys(errors).length
  const offenseChips = useMemo(() => parseOffenseIds(form.offenseIds), [form.offenseIds])

  const hasCredentials = Boolean(config?.config.hasCredentials)
  const err = (path: FieldPath | 'xml') => (attempted ? errors[path] : undefined)
  const recordLoaded = loadedSearchId !== null && isFormPopulated(form)

  const loadRecord = useCallback(async (searchId: string): Promise<PublicRecordsFormInput | null> => {
    const trimmed = searchId.trim()
    if (!trimmed) {
      setLookupError('Enter a search ID')
      return null
    }

    setLoadingRecord(true)
    setLookupError(null)
    try {
      const res = await apiClient.get<LookupResponse>(`/public-records/lookup/${encodeURIComponent(trimmed)}`)
      setForm(res.form)
      setLoadedSearchId(trimmed)
      setAttempted(false)
      setShowDetails(true)
      return res.form
    } catch (e) {
      const message = axios.isAxiosError(e)
        ? (e.response?.data as { message?: string } | undefined)?.message || e.message
        : (e as Error).message
      setLookupError(message || `No record found for search ID ${trimmed}`)
      return null
    } finally {
      setLoadingRecord(false)
    }
  }, [])

  const submit = useCallback(async () => {
    setAttempted(true)
    const trimmedId = form.searchId.trim()
    if (!trimmedId) {
      setLookupError('Enter a search ID')
      return
    }

    let currentForm = form
    if (trimmedId !== loadedSearchId || !isFormPopulated(form)) {
      const loaded = await loadRecord(trimmedId)
      if (!loaded) return
      currentForm = loaded
    }

    if (Object.keys(validate(currentForm, inspectXml(currentForm.xml))).length > 0) return

    setSubmitting(true)
    setResult(null)

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 60_000)
    try {
      const parsed = await apiClient.post<EvaluateResult>('/public-records/evaluate', currentForm, {
        timeout: 60_000,
        signal: controller.signal,
      })
      setResult(parsed)
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data) {
        setResult(e.response.data as EvaluateResult)
      } else {
        const aborted = axios.isAxiosError(e) && e.code === 'ERR_CANCELED'
        setResult({
          ok: false,
          status: 0,
          error: aborted ? 'The evaluation timed out after 60s. Try again.' : (e as Error).message,
        })
      }
    } finally {
      clearTimeout(timeout)
      setSubmitting(false)
    }
  }, [form, loadedSearchId, loadRecord])

  const reset = () => {
    setForm(EMPTY_FORM)
    setLoadedSearchId(null)
    setLookupError(null)
    setShowDetails(false)
    setResult(null)
    setAttempted(false)
  }

  const loadSample = () => {
    setForm(SAMPLE_FORM)
    setLoadedSearchId(SAMPLE_FORM.searchId)
    setLookupError(null)
    setShowDetails(true)
    setResult(null)
    setAttempted(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !submitting && !loadingRecord) {
      e.preventDefault()
      submit()
    }
  }

  const busy = submitting || loadingRecord

  return (
    <div className="space-y-6" onKeyDown={onKeyDown}>
      <SetupStatus data={config} loading={configLoading} onRefresh={refreshConfig} />

      <div className="space-y-6">
        {/* Top: record form */}
        <div className="space-y-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-elevate-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--color-ink)]">Evaluate a public record</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={loadSample}
                className="rounded-md px-2 py-1 text-xs text-[var(--color-muted)] ring-1 ring-[var(--color-border)] transition hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
              >
                Load sample
              </button>
              <button
                onClick={reset}
                className="rounded-md px-2 py-1 text-xs text-[var(--color-muted)] ring-1 ring-[var(--color-border)] transition hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <Field
              label="Search ID"
              required
              value={form.searchId}
              onChange={(v) => {
                set('searchId', v)
                if (loadedSearchId && v.trim() !== loadedSearchId) {
                  setLoadedSearchId(null)
                }
              }}
              error={attempted && !form.searchId.trim() ? 'Required' : undefined}
              placeholder="43060705"
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => loadRecord(form.searchId)}
                disabled={busy || !form.searchId.trim()}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm font-medium text-[var(--color-body)] transition hover:bg-[var(--color-surface-2)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingRecord ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-600" />
                ) : (
                  <Icon name="folder-search" className="h-4 w-4" />
                )}
                {loadingRecord ? 'Loading record…' : 'Load record'}
              </button>
              {recordLoaded ? (
                <span className="text-xs text-emerald-700">Record loaded from BigQuery</span>
              ) : (
                <span className="text-xs text-[var(--color-muted)]">Loads candidate, offenses, and XML from BigQuery bronze</span>
              )}
            </div>
            {lookupError ? (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-200">{lookupError}</p>
            ) : null}
          </div>

          {recordLoaded ? (
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)]/60 px-4 py-3 text-sm text-[var(--color-body)]">
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
                <span><span className="text-[var(--color-muted)]">Date:</span> {form.searchDate}</span>
                <span><span className="text-[var(--color-muted)]">State:</span> {form.applicantState}</span>
                {form.customerState ? <span><span className="text-[var(--color-muted)]">Customer State:</span> {form.customerState}</span> : null}
                {form.searchType ? <span><span className="text-[var(--color-muted)]">Search Type:</span> {form.searchType}</span> : null}
                <span><span className="text-[var(--color-muted)]">Offenses:</span> {offenseChips.length}</span>
                <span><span className="text-[var(--color-muted)]">Candidate:</span> {form.candidate.first_name} {form.candidate.last_name}</span>
              </div>
              <div className="mt-2">
                <PanelToggleButton active={showDetails} onClick={() => setShowDetails((value) => !value)}>
                  {showDetails ? 'Hide' : 'Show'} record details
                </PanelToggleButton>
              </div>
            </div>
          ) : null}

          {showDetails ? (
            <>
          <Section title="Search">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Search date" type="date" required value={form.searchDate} onChange={(v) => set('searchDate', v)} error={err('searchDate')} />
              <Field
                label="Applicant state"
                required
                value={form.applicantState}
                onChange={(v) => set('applicantState', v.toUpperCase().slice(0, 2))}
                placeholder="OR"
                error={err('applicantState')}
              />
              <Field
                label="Customer state"
                value={form.customerState}
                onChange={(v) => set('customerState', v.toUpperCase().slice(0, 2))}
                placeholder="TX"
                error={err('customerState')}
              />
              <Field
                label="Search type"
                value={form.searchType}
                onChange={(v) => set('searchType', v)}
                placeholder="County_criminal"
                error={err('searchType')}
              />
              <Field label="Offense IDs" required value={form.offenseIds} onChange={(v) => set('offenseIds', v)} placeholder="2050, 2051" error={err('offenseIds')} />
            </div>
            {offenseChips.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-[var(--color-subtle)]">Parsed {offenseChips.length}:</span>
                {offenseChips.map((id) => (
                  <span key={id} className="rounded-md bg-[var(--color-surface-2)] px-2 py-0.5 font-mono text-xs text-[var(--color-body)] ring-1 ring-[var(--color-border)]">
                    {id}
                  </span>
                ))}
              </div>
            ) : null}
          </Section>

          <Section title="Candidate">
            <div className="grid grid-cols-3 gap-3">
              <Field label="First name" required value={form.candidate.first_name} onChange={(v) => set('candidate.first_name', v)} error={err('candidate.first_name')} />
              <Field label="Middle" value={form.candidate.middle_name ?? ''} onChange={(v) => set('candidate.middle_name', v)} />
              <Field label="Last name" required value={form.candidate.last_name} onChange={(v) => set('candidate.last_name', v)} error={err('candidate.last_name')} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date of birth" type="date" required value={form.candidate.date_of_birth} onChange={(v) => set('candidate.date_of_birth', v)} error={err('candidate.date_of_birth')} />
              <Field label="SSN" required value={form.candidate.ssn} onChange={(v) => set('candidate.ssn', v)} placeholder="445667890" inputMode="numeric" error={err('candidate.ssn')} />
            </div>
            <Field label="Address" value={form.candidate.address} onChange={(v) => set('candidate.address', v)} />
          </Section>

          <Section title="Screening XML">
            <label className="block">
              <span className="flex items-center justify-between text-xs font-medium text-[var(--color-muted)]">
                <span>
                  Raw record XML<span className="text-emerald-600"> *</span>
                </span>
                {form.xml.trim() ? (
                  xmlInfo.ok ? (
                    <span className="text-emerald-600">well-formed · {xmlInfo.caseCount} case{xmlInfo.caseCount === 1 ? '' : 's'}</span>
                  ) : (
                    <span className="text-red-600">{xmlInfo.error}</span>
                  )
                ) : null}
              </span>
              <textarea
                value={form.xml}
                onChange={(e) => setForm((prev) => ({ ...prev, xml: e.target.value }))}
                rows={6}
                spellCheck={false}
                aria-invalid={err('xml') ? true : undefined}
                className={`scroll-thin mt-1 w-full rounded-lg border bg-[var(--color-code)] px-3 py-2 font-mono text-xs leading-relaxed text-[var(--color-ink)] outline-none transition placeholder:text-[var(--color-subtle)] focus:ring-2 ${
                  err('xml')
                    ? 'border-red-400 focus:border-red-400 focus:ring-red-500/20'
                    : 'border-[var(--color-border)] focus:border-emerald-500 focus:ring-emerald-500/20'
                }`}
              />
            </label>
          </Section>
            </>
          ) : null}

          <div className="space-y-3 pt-1">
            {attempted && errorCount > 0 ? (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-200">
                Fix {errorCount} field{errorCount === 1 ? '' : 's'} above before submitting.
              </p>
            ) : null}
            {!configLoading && !hasCredentials ? (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 ring-1 ring-amber-200">
                No Public Records credentials detected. Set <code className="font-mono">PUBLIC_RECORDS_API_KEY</code> on the API server and restart.
              </p>
            ) : null}

            <button
              onClick={submit}
              disabled={busy || !hasCredentials || !form.searchId.trim()}
              title={!hasCredentials ? 'Configure PUBLIC_RECORDS_API_KEY first' : 'Cmd/Ctrl + Enter'}
              className="btn-shine flex w-full items-center justify-center gap-2 rounded-lg bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_30px_-10px_rgba(16,185,129,0.5)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              ) : (
                <Icon name="bolt" className="h-4 w-4" />
              )}
              {loadingRecord ? 'Loading record…' : submitting ? 'Evaluating…' : 'Evaluate record'}
            </button>
          </div>
        </div>

        {/* Bottom: decision */}
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-2)]/50 p-4 shadow-elevate-sm">
          {busy ? <LoadingState /> : result ? <ResultPanel result={result} searchId={form.searchId} /> : <EmptyState />}
        </div>
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex h-full min-h-[20rem] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[var(--color-border)] text-center">
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-600" />
      <p className="text-sm text-[var(--color-muted)]">Working…</p>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex h-full min-h-[20rem] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--color-border)] text-center">
      <p className="text-sm text-[var(--color-muted)]">No decision yet</p>
      <p className="max-w-xs text-xs text-[var(--color-subtle)]">
        Evaluate a record to see the compliance routing decision — search queue, per-offense reportability, and identity scoring.
      </p>
    </div>
  )
}

function ValidationIssues({ issues }: { issues: unknown[] }) {
  if (!issues.length) return null
  return (
    <div className="space-y-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="text-xs font-semibold text-amber-700">Data quality issues</p>
      <ul className="space-y-1 text-xs text-[var(--color-body)]">
        {issues.map((issue, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-amber-500">•</span>
            <span className="break-words">{typeof issue === 'string' ? issue : JSON.stringify(issue)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function formatFieldValue(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return 'N/A'
  if (typeof raw === 'object' && !Array.isArray(raw) && raw !== null) {
    const obj = raw as Record<string, unknown>
    if ('value' in obj && typeof obj.value === 'string') return obj.value || 'N/A'
    return JSON.stringify(raw)
  }
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No'
  return String(raw)
}

interface UbsLabels {
  ce_decision?: string
  ce_rules_applied?: string
  ce_rationale?: string
  ground_truth?: string
  all_notes?: string
}

function DecisionBadge({ value }: { value?: string }) {
  if (!value) return <span className="text-xs text-gray-400">N/A</span>
  const upper = value.toUpperCase()
  const color =
    upper === 'REPORTABLE' ? 'bg-red-100 text-red-700 ring-red-200'
    : upper === 'NOT_REPORTABLE' ? 'bg-green-100 text-green-700 ring-green-200'
    : 'bg-gray-100 text-gray-700 ring-gray-200'
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ring-1 ${color}`}>
      {upper}
    </span>
  )
}

interface ValidationIssue {
  level?: string
  field?: string
  issue?: string
  description?: string
  offense_id?: string | number
  [key: string]: unknown
}

function OffenseAccordion({ offense, detail, ubsLabels, validationWarnings = [] }: { offense: OffenseDecision; detail?: CeOffenseDetail; ubsLabels?: UbsLabels; validationWarnings?: ValidationIssue[] }) {
  const [open, setOpen] = useState(false)

  const d = detail

  const fields: { label: string; value: unknown; highlight?: boolean }[] = [
    { label: 'Offense ID', value: offense.offense_id },
    { label: 'Case Number', value: d?.case_number },
    { label: 'Charge', value: offense.charge || d?.charge },
    { label: 'Charge Description', value: d?.charge_description },
    { label: 'Charge Normalized', value: d?.charge_norm, highlight: true },
    { label: 'Disposition', value: d?.disposition },
    { label: 'Disposition Class', value: d?.disposition_class, highlight: true },
    { label: 'Disposition Normalized', value: d?.disposition_norm, highlight: true },
    { label: 'Offense Type', value: d?.type },
    { label: 'Type Class', value: d?.type_class, highlight: true },
    { label: 'Subtype Class', value: d?.sub_type_class, highlight: true },
    { label: 'Offense Date', value: d?.charge_date },
    { label: 'Arrest Date', value: d?.arrest_date },
    { label: 'Disposition Date', value: d?.disposition_date },
    { label: 'File Date', value: d?.file_date },
    { label: 'Offense Age', value: d?.offense_age, highlight: true },
    { label: 'Age at Offense', value: d?.age_at_offense },
    { label: 'Age Category', value: d?.age_category, highlight: true },
    { label: 'Offense Status', value: d?.offense_status },
    { label: 'Crime Category', value: d?.crime_category, highlight: true },
    { label: 'Adjudication', value: d?.adjudication_category, highlight: true },
    { label: 'Court Type', value: d?.court_type, highlight: true },
    { label: 'Jurisdiction', value: d?.jurisdiction_state ?? d?.jurisdiction },
    { label: 'Exclusion Applied', value: d?.exclusion_applied == null ? undefined : d.exclusion_applied ? 'Yes' : 'No', highlight: true },
    { label: 'Reportability', value: offense.routing?.reportability, highlight: true },
    { label: 'Identity Level', value: offense.routing?.identity_level },
    { label: 'Identity Score', value: offense.routing?.identity_score },
  ]

  // Evaluate Console always renders live CE offenses. Never fall back to BQ
  // ce_* labels here — empty cited_rules ("") used to OR into stale EXCLUSION_* rows.
  const liveRulesApplied = Array.isArray(offense.cited_rules)
    ? (offense.cited_rules.length > 0 ? offense.cited_rules.join(', ') : 'N/A')
    : ((offense.cited_rules as string) || (offense.rules_applied as string) || 'N/A')
  const hasAiDecision = Boolean(offense.charge_decision || offense.rationale || (Array.isArray(offense.cited_rules) && offense.cited_rules.length > 0) || offense.rules_applied)
  const hasUbsDecision = ubsLabels?.ground_truth

  return (
    <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-[var(--color-surface-2)]"
      >
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm font-semibold text-[var(--color-ink)]">
            {offense.offense_id}
          </span>
          <span className="max-w-md truncate text-sm text-[var(--color-muted)]">{offense.charge || detail?.charge || 'No charge'}</span>
        </div>
        <div className="flex items-center gap-2">
          {offense.charge_decision ? <Badge value={offense.charge_decision} /> : null}
          {offense.routing?.queue ? <Badge value={offense.routing.queue} /> : null}
          <svg
            className={`h-4 w-4 shrink-0 text-[var(--color-subtle)] transition-transform ${open ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {open ? (
        <div className="border-t border-[var(--color-border)] px-4 py-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {fields.map((field) => {
              const v = formatFieldValue(field.value)
              return (
                <div
                  key={field.label}
                  className={
                    field.highlight
                      ? 'rounded-md bg-emerald-50 px-3 py-2 ring-1 ring-emerald-200'
                      : 'px-3 py-2'
                  }
                >
                  <span className={`text-xs font-medium ${field.highlight ? 'text-emerald-700' : 'text-[var(--color-muted)]'}`}>
                    {field.label}
                  </span>
                  <p className={`mt-0.5 text-sm ${field.highlight ? 'font-medium text-emerald-900' : 'text-[var(--color-body)]'}`}>
                    {v}
                  </p>
                </div>
              )
            })}
          </div>

          {(hasAiDecision || hasUbsDecision) ? (
            <div className="mt-4">
              <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--color-ink)]">
                <svg className="h-4 w-4 text-rose-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" /></svg>
                Decisions
              </h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                  <h5 className="mb-2 text-xs font-bold text-indigo-700">AI (Compliance Engine)</h5>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">Decision:</span>
                    <DecisionBadge value={offense.charge_decision} />
                  </div>
                  {offense.routing?.queue ? (
                    <div className="mb-2 flex items-center gap-2">
                      <span className="text-xs text-gray-500">Queue:</span>
                      <span className="text-xs font-medium text-[var(--color-body)]">{offense.routing.queue}</span>
                    </div>
                  ) : null}
                  <div className="mb-2">
                    <span className="text-xs text-gray-500">Rules Applied</span>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-[var(--color-body)]">
                      {liveRulesApplied}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Rationale</span>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-[var(--color-body)]">
                      {(offense.rationale as string) || 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-teal-200 bg-teal-50 p-3">
                  <h5 className="mb-2 text-xs font-bold text-teal-700">UBS (Ground Truth)</h5>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">Decision:</span>
                    <DecisionBadge value={ubsLabels?.ground_truth} />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Notes</span>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-[var(--color-body)]">
                      {ubsLabels?.all_notes || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {validationWarnings.length > 0 ? (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <h5 className="mb-1 text-xs font-bold text-amber-700">Warnings</h5>
                  <ul className="list-inside list-disc space-y-1">
                    {validationWarnings.map((w, i) => (
                      <li key={i} className="text-xs text-amber-800">
                        {w.issue || w.description || 'Unknown warning'}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** Human-friendly hint for common HTTP failure codes from the evaluation API. */
function errorHint(status: number): string | null {
  switch (status) {
    case 401:
      return 'Unauthorized — the CRA token was rejected. Check PUBLIC_RECORDS_API_KEY / PUBLIC_RECORDS_TOKEN (CRA requires an HS256 token).'
    case 403:
      return 'Forbidden — the token is valid but not allowed to evaluate this record.'
    case 422:
      return 'Validation required — the record needs manual review or is missing required data.'
    case 429:
      return 'Rate limit exceeded — wait a moment and try again.'
    case 500:
      return 'Server error — the evaluation pipeline failed. Retry, and contact support if it persists.'
    default:
      return null
  }
}

function PanelToggleButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium shadow-elevate-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${
        active
          ? 'border-emerald-300 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
          : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-body)] hover:border-emerald-300 hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]'
      }`}
    >
      {children}
    </button>
  )
}

interface CeOffenseDetail {
  offense_id?: string
  charge_id?: string
  charge?: string
  charge_description?: string
  charge_norm?: string
  disposition?: string
  disposition_class?: string
  disposition_norm?: string
  type?: string
  type_class?: string
  sub_type_class?: string
  charge_date?: string
  arrest_date?: string
  disposition_date?: string
  file_date?: string
  offense_age?: number
  age_at_offense?: number
  age_category?: string
  offense_status?: string
  crime_category?: string
  adjudication_category?: string
  court_type?: string
  jurisdiction?: string
  jurisdiction_state?: string
  exclusion_applied?: boolean
  additional_info?: string
  sentence_info?: string
  case_number?: string
  [key: string]: unknown
}

function parseCeOffenseMap(ceInput: unknown): Map<string, CeOffenseDetail> {
  const map = new Map<string, CeOffenseDetail>()
  if (!ceInput || typeof ceInput !== 'object') return map

  const root = ceInput as Record<string, unknown>
  const searchResults = (root.criminal_search_results ?? root.court_searches ?? []) as Record<string, unknown>[]

  for (const result of searchResults) {
    const records = (result.criminal_records ?? result.cases ?? []) as Record<string, unknown>[]
    // Search-level fallbacks (CE model uses search_jurisdiction, not jurisdiction_state)
    const searchCourtType = result.court_type as string | undefined
    const searchJurisdiction = result.jurisdiction as string | undefined
    const searchJurisdictionState = result.jurisdiction_state as string | undefined
    const searchJurisdictionCode = result.search_jurisdiction as string | undefined

    for (const record of records) {
      const caseNumber = record.case_number as string | undefined
      // Case/record-level fields in CE/XT payloads (not on offenses[])
      const recordCourtType = (record.court_type ?? searchCourtType) as string | undefined
      const recordFileDate = record.file_date as string | undefined
      const recordJurisdiction = record.jurisdiction as string | undefined
      const recordJurisdictionState = record.jurisdiction_state as string | undefined
      const offenses = (record.offenses ?? []) as CeOffenseDetail[]

      for (const offense of offenses) {
        const id = String(offense.offense_id ?? offense.charge_id ?? '')
        if (!id) continue
        const fallbackState =
          typeof searchJurisdictionCode === 'string'
            ? searchJurisdictionCode.replace(/-+$/, '') || undefined
            : undefined
        map.set(id, {
          ...offense,
          case_number: offense.case_number ?? caseNumber,
          file_date: offense.file_date ?? recordFileDate,
          court_type: offense.court_type ?? recordCourtType,
          jurisdiction:
            offense.jurisdiction ?? recordJurisdiction ?? searchJurisdiction,
          jurisdiction_state:
            offense.jurisdiction_state
            ?? recordJurisdictionState
            ?? searchJurisdictionState
            ?? fallbackState,
        })
      }
    }
  }

  return map
}

function ResultPanel({ result, searchId }: { result: EvaluateResult; searchId: string }) {
  const [showRaw, setShowRaw] = useState(false)
  const [showCeInput, setShowCeInput] = useState(false)
  const [ceInput, setCeInput] = useState<unknown | null>(result.complianceEngineInput ?? null)
  const [ceInputLoading, setCeInputLoading] = useState(false)
  const [ceInputError, setCeInputError] = useState<string | null>(null)
  const [ceAutoLoaded, setCeAutoLoaded] = useState(false)

  const { data: ubsData } = useOffensesBySearchId(searchId || null)

  const ubsLabelsMap = useMemo(() => {
    const map = new Map<string, UbsLabels>()
    const rows = (ubsData as { rows?: Record<string, unknown>[] })?.rows
    if (!rows) return map
    for (const row of rows) {
      const id = String(row.offense_id ?? '')
      if (!id) continue
      map.set(id, {
        ce_decision: (row.ce_decision ?? row.charge_decision) as string | undefined,
        ce_rules_applied: (row.ce_rules_applied ?? row.rules_applied) as string | undefined,
        ce_rationale: (row.ce_rationale ?? row.rationale) as string | undefined,
        ground_truth: row.ground_truth as string | undefined,
        all_notes: row.all_notes as string | undefined,
      })
    }
    return map
  }, [ubsData])

  const loadCeInput = useCallback(async (opts?: { open?: boolean }) => {
    const trimmed = searchId.trim()
    if (!trimmed) return

    setCeInputLoading(true)
    setCeInputError(null)
    try {
      const res = await apiClient.get<{ ok: boolean; complianceEngineInput: unknown }>(
        `/public-records/compliance-input/${encodeURIComponent(trimmed)}`,
      )
      setCeInput(res.complianceEngineInput)
      if (opts?.open !== false) setShowCeInput(true)
    } catch (e) {
      const message = axios.isAxiosError(e)
        ? (e.response?.data as { message?: string } | undefined)?.message || e.message
        : (e as Error).message
      setCeInputError(message || 'Could not load compliance engine input')
    } finally {
      setCeInputLoading(false)
    }
  }, [searchId])

  // Background-fetch CE input for offense detail enrichment after a successful eval.
  // Does not block the evaluate button (API no longer waits on bronze for this).
  useEffect(() => {
    if (ceInput || ceAutoLoaded || ceInputLoading) return
    if (!result.ok || !result.data?.success) return
    setCeAutoLoaded(true)
    void loadCeInput({ open: false })
  }, [ceInput, ceAutoLoaded, ceInputLoading, result.ok, result.data?.success, loadCeInput])

  const offenseDetailsMap = useMemo(() => parseCeOffenseMap(ceInput), [ceInput])

  if (!result.ok || !result.data?.success) {
    const hint = errorHint(result.status)
    return (
      <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="text-sm font-semibold text-red-700">Evaluation failed{result.status ? ` (HTTP ${result.status})` : ''}</div>
        {hint ? <p className="text-xs text-amber-700">{hint}</p> : null}
        <p className="break-words text-xs text-[var(--color-body)]">{result.error ?? 'The evaluation API returned an unsuccessful response.'}</p>
        {result.data ? <JsonView value={result.data} maxHeight="20rem" /> : null}
      </div>
    )
  }

  const data = result.data.data
  const decision = data?.decision
  const issues = (data?.validation?.issues ?? []) as unknown[]

  const offenseWarningsMap = useMemo(() => {
    const map = new Map<string, ValidationIssue[]>()
    const raw = data?.validation?.issues as { errors?: ValidationIssue[]; warnings?: ValidationIssue[] } | ValidationIssue[] | undefined
    if (!raw) return map

    const allIssues: ValidationIssue[] = []
    if (Array.isArray(raw)) {
      allIssues.push(...raw)
    } else {
      if (raw.errors) allIssues.push(...raw.errors)
      if (raw.warnings) allIssues.push(...raw.warnings)
    }

    for (const issue of allIssues) {
      const oid = issue.offense_id != null ? String(issue.offense_id) : null
      if (!oid || issue.level !== 'offense') continue
      const list = map.get(oid) ?? []
      list.push(issue)
      map.set(oid, list)
    }
    return map
  }, [data])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
          <Icon name="check" className="h-4 w-4" /> Evaluation complete
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <PanelToggleButton
            active={showCeInput}
            disabled={ceInputLoading}
            onClick={() => {
              if (ceInput) {
                setShowCeInput((s) => !s)
              } else {
                void loadCeInput()
              }
            }}
          >
            {ceInputLoading ? (
              <span className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-emerald-200 border-t-emerald-600" />
            ) : null}
            {ceInputLoading ? 'Loading…' : showCeInput ? 'Hide' : 'Show'} CE input
          </PanelToggleButton>
          <PanelToggleButton active={showRaw} onClick={() => setShowRaw((s) => !s)}>
            {showRaw ? 'Hide' : 'Show'} response
          </PanelToggleButton>
        </div>
      </div>

      {decision ? (
        <div className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {decision.search_queue ? (
              <div className="space-y-1">
                <p className="text-xs text-[var(--color-muted)]">Search queue</p>
                <Badge value={decision.search_queue} />
              </div>
            ) : null}
            {decision.record_decision ? (
              <div className="space-y-1">
                <p className="text-xs text-[var(--color-muted)]">Record decision</p>
                <Badge value={decision.record_decision} />
              </div>
            ) : null}
            {data?.status ? (
              <div className="space-y-1">
                <p className="text-xs text-[var(--color-muted)]">Status</p>
                <Badge value={data.status} />
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-xs text-[var(--color-muted)]">
          The API returned success but no decision payload. See the raw JSON for details.
        </p>
      )}

      <ValidationIssues issues={issues} />

      {decision?.court_decisions?.map((court, ci) => (
        <div key={court.court_search_id ?? ci} className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-[var(--color-muted)]">{court.court_search_id}</span>
            {court.court_queue ? <Badge value={court.court_queue} /> : null}
          </div>
          {court.case_decisions?.map((c, idx) => (
            <div key={c.case_number ?? idx} className="space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-[var(--color-body)]">
                  Case <span className="font-mono">{c.case_number}</span>
                </span>
                {c.case_queue ? <Badge value={c.case_queue} /> : null}
              </div>
              <div className="space-y-2">
                {c.offenses?.map((o, oi) => (
                  <OffenseAccordion key={o.offense_id ?? oi} offense={o} detail={offenseDetailsMap.get(String(o.offense_id))} ubsLabels={ubsLabelsMap.get(String(o.offense_id))} validationWarnings={offenseWarningsMap.get(String(o.offense_id)) ?? []} />
                ))}

              </div>
            </div>
          ))}
        </div>
      ))}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {data?.search_id ? (
          <div className="flex justify-between gap-2">
            <dt className="text-[var(--color-muted)]">search_id</dt>
            <dd className="font-mono text-[var(--color-body)]">{data.search_id}</dd>
          </div>
        ) : null}
        {data?.correlation_id ? (
          <div className="flex justify-between gap-2">
            <dt className="text-[var(--color-muted)]">correlation_id</dt>
            <dd className="truncate font-mono text-[var(--color-body)]">{data.correlation_id}</dd>
          </div>
        ) : null}
        {data?.timing?.total_ms !== undefined ? (
          <div className="flex justify-between gap-2">
            <dt className="text-[var(--color-muted)]">total_ms</dt>
            <dd className="font-mono text-[var(--color-body)]">{data.timing.total_ms}</dd>
          </div>
        ) : null}
      </dl>

      {ceInputError ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 ring-1 ring-amber-200">
          {ceInputError}{' '}
          <button type="button" onClick={() => void loadCeInput()} className="underline">
            Retry
          </button>
        </p>
      ) : null}

      {showCeInput && ceInput ? (
        <div className="space-y-2">
          <p className="text-xs text-[var(--color-muted)]">XML-transformer JSON POSTed to the compliance engine</p>
          <JsonView value={ceInput} maxHeight="24rem" />
        </div>
      ) : null}

      {showRaw ? <JsonView value={result.data} maxHeight="24rem" /> : null}
    </div>
  )
}
